import {
  canonicalAddress,
  canonicalEntry,
  type CanonicalEntry,
} from "./canonical-entry.js";

/**
 * Asking the site about one word, and what counts as it having answered.
 *
 * Apart from `glosses.ts` because that file is the policy. Which answers are
 * held, how long a stored one is given for and what happens when a request
 * fails are decisions; this is the request they are made about.
 */

/**
 * How long either caller waits on one entry object.
 *
 * A `canonical.json` is a few hundred bytes behind the edge cache, so anything
 * approaching this is a network that has stopped answering rather than one
 * that is slow. Without a deadline a request that never settles leaves a word
 * held forever: an extension menu item that appears to do nothing, or a row of
 * the segmenter's list whose meaning never arrives.
 *
 * The extension's `requestDeadlineMs` is the same ten seconds and is not this
 * one. That covers the pointer at `/extension.json`, which only the extension
 * fetches, and the two are separate for the reason `fa-icon-map.ts` gives
 * about one picture under two names: a later change to either should not have
 * to be untangled from the other.
 */
export const glossDeadlineMs = 10_000;

/**
 * The entry a response carries, or nothing where its body is not one.
 *
 * A body that fails to parse counts as the site having answered, the same as a
 * 404. Asking again would fetch the same bytes and fail on them again.
 */
async function entryIn(
  response: Response,
): Promise<CanonicalEntry | undefined> {
  try {
    return canonicalEntry(await response.json());
  } catch {
    return undefined;
  }
}

/** Where the entry for a word is, on a site with no trailing slash. */
export function glossUrl(siteUrl: string, word: string): string {
  return `${siteUrl}${canonicalAddress(word)}`;
}

/**
 * One request, throwing where it failed to arrive.
 *
 * A response of any status is the site answering, and a 404 is the answer for
 * the great majority of what a page holds. What escapes is a request that
 * never became a response: a dropped connection, a refused DNS answer, or the
 * deadline. That deadline is what stops a request that never settles holding a
 * word forever, a worker waiting on one having a menu item that appears to do
 * nothing.
 */
export async function askedSite(
  get: typeof globalThis.fetch,
  url: string,
): Promise<CanonicalEntry | undefined> {
  const response = await get(url, {
    signal: AbortSignal.timeout(glossDeadlineMs),
  });

  return response.ok ? await entryIn(response) : undefined;
}
